import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AnonymusDocOnlineService } from "../../services/AnonymusDocOnlineService";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { translateAndSortAscendant } from "../../common/helper";

const DOCOnlineContext = createContext();

export default function DOCOnlineProvider({ children }) {
  const [errors, setErrors] = useState([{}]);
  const [tokenInvalid, setTokenInvalid] = useState(undefined);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [autoCompleteSchoolData, setAutoCompleteSchoolData] = useState([{}]);
  const [autoCompleteCourseData, setAutoCompleteCourseData] = useState([{}]);
  const [currentStep, setCurrentStep] = useState(1);
  const [yearsExperience, setYearexperience] = useState([]);
  const [skillLevels, setSkillLevels] = useState([]);
  const [techSkills, setTechSkills] = useState([]);
  const [comboBoxList, setComboBoxList] = useState([]);
  const [docOnline, setDocOnline] = useState({
    id: 0,
    token: "",
    isAcceptedConsent: false,
    cv: "",
    companyId: 0,
    companyAbbreviation: "",
    generatedLanguageInitials: "",
    personalInformation: {
      fullName: "",
      birthDate: "",
      jobRoleId: 0,
      gitHub: "",
      hobbies: "",
      yearsOfExperience: "",
      resume: ""
    },
    educationAndTraining: [{}],
    motherLanguage: 0,
    otherLanguages: [{}],
    workExperience: [{}],
    competences: [],
    otherCompetences: [],
    welcomeMessage: "",
    description: ""
  });

  const [jobRoles, setJobRoles] = useState([]);
  const [zones, setZone] = useState([{}]);
  const [maritalStatus, setMaritalStatus] = useState([{}]);
  const [Languages, setLaguages] = useState([]);
  const [LanguageLevels, setLanguageLevels] = useState([]);
  const [docOnlineRGPDMessage, setDocOnlineRGPDMessage] = useState({});
  const { t, i18n } = useTranslation();
  const setLanguage = async (definedLanguage) => {
    return i18n.changeLanguage(definedLanguage);
  };
  const [submitDocOnlineIsDisabled, setSubmitDocOnlineIsDisabled] = useState(
    false
  );

  async function GetDocOnlineByToken(token) {
    AnonymusDocOnlineService.getDocOnlineByToken(token).then(async(data) => {
      if (!data.error) {
        await setLanguage(data.generatedLanguageInitials);
        setTokenInvalid(false);
        let _docOnline = { ...docOnline };
        let { birthDate } = _docOnline.personalInformation;

        _docOnline.id = data.id;
        _docOnline.isAcceptedConsent = data.isAcceptedConsent;
        _docOnline.companyId = data.companyId;
        _docOnline.generatedLanguageInitials = data.generatedLanguageInitials;
        _docOnline.companyAbbreviation = data.companyAbbreviation;
        _docOnline.personalInformation = data.personalInformation;
        _docOnline.companyPhoneNumber = data.companyPhoneNumber;
        _docOnline.companyEmailAddress = data.companyEmailAddress;
        _docOnline.welcomeMessage = data.welcomeMessage;
        _docOnline.isConsultant = data.isConsultant;
        _docOnline.description = data.description;
        if (moment(birthDate).isValid()) {
          birthDate = moment(birthDate).toDate();
        } else {
          birthDate = "";
        }

        if (
          data.docEducations === undefined ||
          Object.keys(data.docEducations).length === 0
        ) {
          _docOnline.educationAndTraining = [{}];
        } else {
          _docOnline.educationAndTraining = data.docEducations;
        }

        if (
          data.docWorkExperiences === undefined ||
          Object.keys(data.docWorkExperiences).length === 0
        ) {
          _docOnline.workExperience = [{}];
        } else {
          _docOnline.workExperience = data.docWorkExperiences;
        }

        _docOnline.motherLanguage = data.motherLanguageId;

        if (data.docLanguages.length !== 0) {
          _docOnline.otherLanguages = data.docLanguages;
        } else {
          _docOnline.otherLanguages = [];
        }

        _docOnline.competences = data.competences;
        _docOnline.otherCompetences = data.otherCompetences;
        _docOnline.token = token;
        _docOnline.displayedLanguage = data.generatedLanguageInitials;
        _docOnline.candidacyCompany = data.companyAbbreviation;
        setDocOnline(_docOnline);
        

        setLanguageLevels(
          translateAndSortAscendant(
            data.dropDownLists.languageLevels,
            "description"
          )
        );
        setMaritalStatus(data.dropDownLists.maritalStatus);
        setAutoCompleteSchoolData(
          data.dropDownLists.schools.map((school) => {
            return {
              id: school.id,
              name: school.description
            };
          })
        );

        setLaguages(
          translateAndSortAscendant(data.dropDownLists.languages, "description")
        );
        setAutoCompleteCourseData(
          data.dropDownLists.courses.map((course) => {
            return {
              id: course.id,
              name: course.description
            };
          })
        );

        setJobRoles(
          translateAndSortAscendant(data.dropDownLists.jobRoles, "description")
        );
        setZone(data.dropDownLists.zones);
        setTechSkills(translateAndSortAscendant(data.dropDownLists.technologies,"description"));
        setComboBoxList(translateAndSortAscendant(data.dropDownLists.technologies.map((x) => {return { key: x.id, description: x.description };}), "description"));
        setYearexperience(data.dropDownLists.skillExperienceTimes);
        setSkillLevels(data.dropDownLists.skillLevels);
        setDocOnlineRGPDMessage({ message: data.dropDownLists.rgpd });

        if (!data.isAcceptedConsent) {
          setShowConsentModal(true);
        }
      } else {
        setTokenInvalid(true);
      }
    });
  }

  const AcceptRGPD = () => {
    setDocOnline({
      ...docOnline,
      isAcceptedConsent: true
    });
    AnonymusDocOnlineService.AcceptRGPD(docOnline.token).catch((err) => {
      console.error(err);
      toast.error(t("Something went wrong"));
    });
  };

  function canAddNewEducationExperience() {
    let lastItemIndex = docOnline.educationAndTraining.length - 1;
    if (
      Object.keys(docOnline.educationAndTraining[lastItemIndex]).length === 0 ||
      Object.values(docOnline.educationAndTraining[lastItemIndex])
        .filter((item) => typeof item !== "boolean")
        .every((item) => item === undefined) === true
    ) {
      return false;
    } else {
      return true;
    }
  }

  function canAddNewWorkExperience() {
    let lastItemIndex = docOnline.workExperience.length - 1;
    if (Object.keys(docOnline.workExperience[lastItemIndex]).length === 0) {
      return false;
    } else {
      return true;
    }
  }

  const yearsOfExperienceIsValid = (value) => {
    if (value) {
      let yearsOfExperience = parseInt(value);
      if (isNaN(yearsOfExperience) === false) {
        if (yearsOfExperience >= 0 && yearsOfExperience <= 50) {
          return true;
        }
      }
    }
    return false;
  };

  const resumeLenghtValid = (value) => {
    if (value) {
      let resumeLenght = parseInt(value.length);
      if (resumeLenght >= 30) {
        return true;
      }
    }
    return false;
  };

  const checkPersonalInfoErrors = () => {
    const {
      fullName,
      birthDate,
      jobRoleId,
      yearsOfExperience,
      resume
    } = docOnline.personalInformation;
    const personalInfoErrors = {};

    if (resumeLenghtValid(resume) === false) {
      personalInfoErrors.resume = t("Minimum 30 characters required.");
    }

    if (!fullName) {
      personalInfoErrors.fullName = t("The Full Name is required.");
    }

    if (!birthDate) {
      personalInfoErrors.birthDate = t("The BirthDate is required.");
    }

    if (!jobRoleId) {
      personalInfoErrors.jobRoleId = t("The Job Role is required.");
    }

    if (!yearsOfExperience) {
      personalInfoErrors.yearsOfExperience = t(
        "The Years of Experience is required."
      );
    } else {
      if (yearsOfExperienceIsValid(yearsOfExperience) === false) {
        personalInfoErrors.yearsOfExperience = t(
          "The Years of Experience is required."
        );
      }
    }
    return personalInfoErrors;
  };

  const checkEducationErrors = () => {
    const educationErrors = {
      otherLanguagesErr: [],
      educationAndTrainingErr: []
    };
    const { motherLanguage, otherLanguages, educationAndTraining } = docOnline;

    if (
      motherLanguage === null ||
      motherLanguage === "" ||
      motherLanguage === undefined
    ) {
      educationErrors.motherLanguageErr = t("The Native Language is required.");
    }

    otherLanguages.forEach((item, index) => {
      if (!item.languageId) {
        educationErrors.otherLanguagesErr[index] = {
          ...educationErrors.otherLanguagesErr[index],
          languageId: t("The Language is required ")
        };
      }
      if (!item.languageLevelId) {
        educationErrors.otherLanguagesErr[index] = {
          ...educationErrors.otherLanguagesErr[index],
          languageLevelId: t("The language level is required.")
        };
      }
    });

    educationAndTraining.forEach((item, index) => {
      if (
        Object.keys(item).length > 0 &&
        Object.values(docOnline.educationAndTraining[index])
          .filter((item) => typeof item !== "boolean")
          .every((item) => item === undefined) === false
      ) {
        if (!item.courseName && !item.courseId) {
          educationErrors.educationAndTrainingErr[index] = {
            ...educationErrors.educationAndTrainingErr[index],
            course: t("The course is required.")
          };
        }

        if (!item.schoolName) {
          educationErrors.educationAndTrainingErr[index] = {
            ...educationErrors.educationAndTrainingErr[index],
            school: t("The school is required.")
          };
        }

        if (!item.startDate) {
          educationErrors.educationAndTrainingErr[index] = {
            ...educationErrors.educationAndTrainingErr[index],
            startDate: t("The start date is required.")
          };
        }

        if (item.startDate && item.endDate) {
          if (moment(item.endDate).isBefore(item.startDate)) {
            educationErrors.educationAndTrainingErr[index] = {
              ...educationErrors.educationAndTrainingErr[index],
              endDate: t("The end date is invalid.")
            };
          }
        }
      }
    });

    if (
      !!educationErrors.motherLanguageErr ||
      educationErrors.otherLanguagesErr.length > 0 ||
      educationErrors.educationAndTrainingErr.length > 0
    ) {
      return educationErrors;
    } else {
      return {};
    }
  };

  const checkWorkExpErrors = () => {
    const workExpErrors = { workExperience: [] };
    const { workExperience } = docOnline;

    workExperience.forEach((item, index) => {
      if (
        item.occupationOrPositionHeld ||
        item.companyOrEmployName ||
        item.startDate ||
        item.endDate
      ) {
        if (!item.occupationOrPositionHeld) {
          workExpErrors.workExperience[index] = {
            occupationOrPositionHeld: t(
              "The Occupation or position held is required."
            )
          };
        }

        if (!item.companyOrEmployName) {
          workExpErrors.workExperience[index] = {
            ...workExpErrors.workExperience[index],
            companyOrEmployName: t("The Company/Employer name is required.")
          };
        }

        if (!item.startDate) {
          workExpErrors.workExperience[index] = {
            ...workExpErrors.workExperience[index],
            startDate: t("The Start Date is required.")
          };
        }

        if (!item.endDate) {
          if (!item.projectStillGoing) {
            workExpErrors.workExperience[index] = {
              ...workExpErrors.workExperience[index],
              endDate: t("The End Date is required")
            };
          }
        }

        if (item.startDate && item.endDate) {
          if (item.startDate > item.endDate) {
            workExpErrors.workExperience[index] = {
              ...workExpErrors.workExperience[index],
              endDate: t("The End Date is invalid")
            };
          }
        }
      }
    });

    if (workExpErrors.workExperience.length === 0) {
      return {};
    } else {
      return workExpErrors;
    }
  };

  const checkTechSkillsErrors = () => {
    const techSkillErrors = { competences: [] };
    const { competences, otherCompetences } = docOnline;

    if (
      competences &&
      competences.length < 1 &&
      otherCompetences &&
      otherCompetences.length < 1
    ) {
      techSkillErrors.DOCOnlineTechSkill = t(
        "At least one technical skill is required."
      );
    }

    competences.forEach((item) => {
      if (!item.skillLevelId) {
        techSkillErrors.competences[item.skillId] = {
          ...techSkillErrors.competences[item.skillId],
          skillLevelId: t("Select the level of experience")
        };
      }

      if (!item.skillExperienceTimeId) {
        techSkillErrors.competences[item.skillId] = {
          ...techSkillErrors.competences[item.skillId],
          skillExperienceTimeId: t("Select the experience time")
        };
      }
    });

    if (otherCompetences && otherCompetences.length > 0) {
      techSkillErrors.otherCompetences = otherCompetences.reduce(
        (accumulator, item, index) => {
          const error = {};

          if (!item.skillLevelId) {
            error.skillLevelId = t("Select the level of experience");
          }

          if (!item.skillExperienceTimeId) {
            error.skillExperienceTimeId = t("Select the experience time");
          }

          if (Object.keys(error).length > 0) {
            accumulator.push(error); 
          }

          return accumulator;
        },
        []
      );
    }

    if (
      Object.keys(techSkillErrors.competences)?.length === 0 &&
      (!techSkillErrors.otherCompetences || techSkillErrors.otherCompetences.length === 0) &&
      !techSkillErrors.DOCOnlineTechSkill
    ) {
      return {};
    } else {
      return techSkillErrors;
    }
  };

  const isRequiredFieldsFilled = () => {
    const personalInfoErrors = checkPersonalInfoErrors();
    const educationErrors = checkEducationErrors();
    const workExperienceErrors = checkWorkExpErrors();
    const techSkillErrors = checkTechSkillsErrors();
    const docOnlineErrors = {
      ...personalInfoErrors,
      ...educationErrors,
      ...workExperienceErrors,
      ...techSkillErrors
    };

    setErrors(docOnlineErrors);

    return Object.keys(docOnlineErrors).length === 0 ? true : false;
  };

  const SubmitDoc = () => {
    if (isRequiredFieldsFilled() === false) {
      toast.warning(`${t("Some mandatory fields were not filled.")}`);
      return;
    }

    setSubmitDocOnlineIsDisabled(true);
    // Filtering educationAndTraining object for completely empty fields before sending to backend.
    docOnline.educationAndTraining = docOnline.educationAndTraining.filter(
      (educationInfo) =>
        Object.keys(educationInfo).length !== 0 &&
        Object.values(educationInfo)
          .filter((info) => typeof info !== "boolean")
          .filter((info) => info !== undefined).length !== 0
    );

    AnonymusDocOnlineService.Submit(docOnline).then((result) => {
      if (result === undefined) {
        setCurrentStep(7);
      } else if (result.error) {
        toast.error(t("Something went wrong."));
        setSubmitDocOnlineIsDisabled(false);
      }
    });
  };

  return (
    <DOCOnlineContext.Provider
      value={{
        docOnline,
        setDocOnline,
        showConsentModal,
        setShowConsentModal,
        maritalStatus,
        currentStep,
        setCurrentStep,
        jobRoles,
        Languages,
        LanguageLevels,
        errors,
        setErrors,
        GetDocOnlineByToken,
        AcceptRGPD,
        SubmitDoc,
        canAddNewEducationExperience,
        canAddNewWorkExperience,
        tokenInvalid,
        autoCompleteCourseData,
        autoCompleteSchoolData,
        checkEducationErrors,
        checkPersonalInfoErrors,
        checkTechSkillsErrors,
        checkWorkExpErrors,
        docOnlineRGPDMessage,
        zones,
        skillLevels,
        yearsExperience,
        yearsOfExperienceIsValid,
        techSkills,
        comboBoxList,
        submitDocOnlineIsDisabled
      }}
    >
      {children}
    </DOCOnlineContext.Provider>
  );
}

export function useDOCOnlineContext() {
  const context = useContext(DOCOnlineContext);
  return context;
}
